import React, { useState, useEffect } from 'react';
import { Card, DropZone, BlockStack, Text, Banner, Spinner, InlineStack, ButtonGroup, Button, InlineGrid, Modal, Box } from '@shopify/polaris';
import { getRequest } from '../../../shared/components/functions';

export default function InteractionDocuments({ interactionId }) {
  const [documents, setDocuments] = useState([]);
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState('');
  const [uploadError, setUploadError] = useState('');
  const [loading, setLoading] = useState(true);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewingDocument, setViewingDocument] = useState(null);
  const [viewDocumentUrl, setViewDocumentUrl] = useState(null);
  const [viewDocumentLoading, setViewDocumentLoading] = useState(false);

  useEffect(() => {
    if (interactionId) {
      loadDocuments();
    }
  }, [interactionId]);

  const loadDocuments = async () => {
    if (!interactionId) return;

    setLoading(true);
    try {
      const data = await getRequest(`/api/interactions/${interactionId}`);
      console.log('Loaded interaction data:', data);
      console.log('S3ObjectKeys value:', data?.S3ObjectKeys);
      console.log('S3ObjectKeys type:', typeof data?.S3ObjectKeys);

      // Check for S3ObjectKeys in various possible formats
      const s3Keys = data?.S3ObjectKeys || data?.s3ObjectKeys || data?.S3_ObjectKeys || data?.s3_object_keys;

      if (s3Keys) {
        try {
          // If it's already an array, use it directly
          let parsed;
          if (Array.isArray(s3Keys)) {
            parsed = s3Keys;
          } else if (typeof s3Keys === 'string') {
            parsed = JSON.parse(s3Keys);
          } else {
            parsed = s3Keys;
          }

          console.log('Parsed documents:', parsed);
          setDocuments(Array.isArray(parsed) ? parsed : []);
        } catch (e) {
          console.error('Error parsing S3ObjectKeys:', e, 'Raw value:', s3Keys);
          setDocuments([]);
        }
      } else {
        console.log('No S3ObjectKeys found in data');
        setDocuments([]);
      }
    } catch (error) {
      console.error('Error loading documents:', error);
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDocumentUpload = async (files) => {
    if (!interactionId) {
      alert('No interaction ID available');
      return;
    }

    // Polaris DropZone passes files directly, not as {accepted, rejected}
    const file = files?.[0];
    if (!file) return;

    setUploadingDocument(true);
    setUploadError('');
    setUploadSuccess('');

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`/api/interactions/${interactionId}/documents`, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Upload failed' }));
        throw new Error(errorData.message || 'Upload failed');
      }

      const result = await response.json().catch(() => ({}));

      // Optimistically update local state with returned document
      if (result && result.document) {
        setDocuments((prev) => [...prev, result.document]);
      } else {
        // Fallback to reloading if backend didn't return the document object
        await loadDocuments();
      }

      setUploadSuccess(`Document "${file.name}" uploaded successfully!`);

      // Clear success message after 5 seconds
      setTimeout(() => setUploadSuccess(''), 5000);
    } catch (error) {
      console.error('Error uploading document:', error);
      setUploadError(error.message || 'Error uploading document');
    } finally {
      setUploadingDocument(false);
    }
  };

  const canDisplayInline = (mimetype, filename) => {
    if (!mimetype && filename) {
      // Try to infer from filename extension
      const ext = filename.split('.').pop()?.toLowerCase();
      const inlineableExts = ['pdf', 'jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'txt', 'csv'];
      return inlineableExts.includes(ext);
    }

    if (!mimetype) return false;

    // Check if mimetype is viewable inline
    const inlineableTypes = ['application/pdf', 'image/', 'text/', 'application/json', 'application/xml', 'text/csv'];

    return inlineableTypes.some((type) => mimetype.startsWith(type));
  };

  const handleDocumentView = async (documentIndex) => {
    if (!interactionId) return;

    const document = documents[documentIndex];
    if (!document) return;

    const canInline = canDisplayInline(document.mimetype, document.filename);

    setViewDocumentLoading(true);
    setViewingDocument(document);

    try {
      const response = await fetch(`/api/interactions/${interactionId}/documents/${documentIndex}/url`, {
        method: 'POST'
      });

      if (!response.ok) {
        throw new Error('Failed to generate download URL');
      }

      const data = await response.json();

      if (canInline) {
        // Try to display inline
        setViewDocumentUrl(data.url);
        setViewModalOpen(true);
      } else {
        // Download directly for unsupported types
        const link = window.document.createElement('a');
        link.href = data.url;
        link.download = document.filename || 'document';
        window.document.body.appendChild(link);
        link.click();
        window.document.body.removeChild(link);
      }
    } catch (error) {
      console.error('Error viewing document:', error);
      alert(`Error opening document: ${error.message}`);
    } finally {
      setViewDocumentLoading(false);
    }
  };

  const handleDownloadDocument = async (documentIndex) => {
    if (!interactionId) return;

    const document = documents[documentIndex];
    if (!document) return;

    try {
      const response = await fetch(`/api/interactions/${interactionId}/documents/${documentIndex}/url`, {
        method: 'POST'
      });

      if (!response.ok) {
        throw new Error('Failed to generate download URL');
      }

      const data = await response.json();
      const link = window.document.createElement('a');
      link.href = data.url;
      link.download = document.filename || 'document';
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading document:', error);
      alert(`Error downloading document: ${error.message}`);
    }
  };

  const handleDocumentDelete = async (documentIndex) => {
    if (!interactionId) return;
    if (!confirm('Are you sure you want to delete this document?')) return;

    try {
      const response = await fetch(`/api/interactions/${interactionId}/documents/${documentIndex}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        throw new Error('Failed to delete document');
      }

      await loadDocuments(); // Reload documents list
      setUploadSuccess('Document deleted successfully.');
      setTimeout(() => setUploadSuccess(''), 5000);
    } catch (error) {
      console.error('Error deleting document:', error);
      setUploadError(`Error deleting document: ${error.message}`);
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown size';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  if (loading) {
    return (
      <Card>
        <BlockStack gap="400">
          <Spinner size="small" />
          <Text as="p" tone="subdued">
            Loading documents...
          </Text>
        </BlockStack>
      </Card>
    );
  }

  return (
    <Card>
      <BlockStack gap="400">
        <Text variant="headingMd" as="h3">
          Documents
        </Text>

        {uploadSuccess && (
          <Banner status="success" onDismiss={() => setUploadSuccess('')}>
            {uploadSuccess}
          </Banner>
        )}

        {uploadError && (
          <Banner status="critical" onDismiss={() => setUploadError('')}>
            {uploadError}
          </Banner>
        )}

        {uploadingDocument && (
          <Banner status="info">
            <InlineStack align="start" gap="200">
              <Spinner size="small" />
              <Text as="span">Uploading document...</Text>
            </InlineStack>
          </Banner>
        )}

        <DropZone onDrop={handleDocumentUpload} allowMultiple={false} disabled={uploadingDocument}>
          <DropZone.FileUpload actionHint="Accepts all file types" />
        </DropZone>

        {documents.length > 0 && (
          <BlockStack gap="400">
            <Text variant="bodyMd" as="p" fontWeight="semibold">
              Uploaded Documents ({documents.length}):
            </Text>
            <BlockStack gap="300">
              {documents.map((doc, index) => (
                <Card key={index}>
                  <InlineGrid columns={{ xs: 1, sm: 2, md: 3, lg: 3, xl: 3 }}>
                    <BlockStack gap="100">
                      <Text as="p" variant="bodyMd" fontWeight="medium">
                        {doc.filename || 'Unknown file'}
                      </Text>
                      <InlineStack gap="200">
                        <Text as="span" variant="bodySm" tone="subdued">
                          {formatFileSize(doc.size)}
                        </Text>
                        {doc.uploadedAt && (
                          <Text as="span" variant="bodySm" tone="subdued">
                            • {new Date(doc.uploadedAt).toLocaleString()}
                          </Text>
                        )}
                      </InlineStack>
                    </BlockStack>
                    <ButtonGroup>
                      <Button onClick={() => handleDocumentView(index)} loading={viewDocumentLoading && viewingDocument === doc}>
                        View
                      </Button>
                      <Button onClick={() => handleDownloadDocument(index)}>Download</Button>
                      <Button destructive onClick={() => handleDocumentDelete(index)}>
                        Delete
                      </Button>
                    </ButtonGroup>
                  </InlineGrid>
                </Card>
              ))}
            </BlockStack>
          </BlockStack>
        )}

        {documents.length === 0 && !uploadingDocument && (
          <Text as="p" tone="subdued" variant="bodySm">
            No documents uploaded yet. Use the drop zone above to upload files.
          </Text>
        )}
      </BlockStack>

      {/* Document View Modal */}
      <Modal
        open={viewModalOpen}
        onClose={() => {
          setViewModalOpen(false);
          setViewDocumentUrl(null);
          setViewingDocument(null);
        }}
        title={viewingDocument?.filename || 'View Document'}
        size="large"
      >
        <Modal.Section>
          {viewDocumentLoading ? (
            <Box padding="400">
              <InlineStack align="center" gap="200">
                <Spinner size="small" />
                <Text>Loading document...</Text>
              </InlineStack>
            </Box>
          ) : viewDocumentUrl ? (
            <Box padding="400">
              {viewingDocument?.mimetype?.startsWith('image/') ? (
                <div style={{ textAlign: 'center' }}>
                  <img
                    src={viewDocumentUrl}
                    alt={viewingDocument?.filename}
                    style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
                    onError={(e) => {
                      console.error('Error loading image, falling back to download');
                      e.target.style.display = 'none';
                      handleDownloadDocument(documents.indexOf(viewingDocument));
                      setViewModalOpen(false);
                    }}
                  />
                </div>
              ) : (
                <div style={{ width: '100%', height: '70vh', border: '1px solid #e1e3e5' }}>
                  <iframe
                    title={viewingDocument?.filename || 'Document Viewer'}
                    src={viewDocumentUrl}
                    style={{ width: '100%', height: '100%', border: 'none' }}
                    onError={() => {
                      console.error('Error loading document in iframe, falling back to download');
                      handleDownloadDocument(documents.indexOf(viewingDocument));
                      setViewModalOpen(false);
                    }}
                  />
                </div>
              )}
              <Box paddingBlockStart="400">
                <ButtonGroup>
                  <Button onClick={() => handleDownloadDocument(documents.indexOf(viewingDocument))}>Download</Button>
                  <Button
                    onClick={() => {
                      setViewModalOpen(false);
                      setViewDocumentUrl(null);
                      setViewingDocument(null);
                    }}
                  >
                    Close
                  </Button>
                </ButtonGroup>
              </Box>
            </Box>
          ) : null}
        </Modal.Section>
      </Modal>
    </Card>
  );
}
